import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { noop } from 'lodash-es'
import {
  EntityIdAndType,
  EntityType,
  EntityTypeEnum,
  FxRate,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  WorkflowActions
} from 'api'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { FxRateHelper } from 'model'
import { Locale, Translation } from 'locale'
import { fxRateFieldLength } from 'view/styles/constants'

export type FxRateDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  fxRateEntity: ReferenceDataEntityType<FxRate> | null
  availableEvents: ReferenceDataManagementEvent[]
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: FxRate; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: FxRateDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: FxRateDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  fxRateEntity: createWorkflowReferenceDataEntitySelector(EntityType.FX_RATE, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const FxRateDialogComponent: React.FunctionComponent<FxRateDialogProps> = props => {
  const { workflowId, fxRateEntity, availableEvents, onUpdate, onSubmitWorkflow, onCancelWorkflow, onClose } = props

  if (!fxRateEntity) {
    return null
  }

  const fxRate = fxRateEntity.data

  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const hasValidationIssues = !!fxRateEntity.validationIssues && fxRateEntity.validationIssues.length > 0

  const onUpdateFxRate = (rate: number) => {
    onUpdate({
      entityId: fxRate.id,
      entityType: EntityTypeEnum.FX_RATE,
      updatedEntity: { ...fxRate, rate },
      workflowId
    })
  }

  const onSave = () => {
    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={FxRateHelper.getName(fxRate)}>
      <ModalContent>
        <DialogRow>
          <DialogField>
            <TextInputField
              label={Locale.translate(Translation.TC_CURRENCY_PAIR)}
              readOnly={true}
              value={FxRateHelper.getName(fxRate)}
              onUpdate={noop}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              label={Locale.translate(Translation.TC_FX_RATE)}
              fieldName="rate"
              maxLength={fxRateFieldLength}
              readOnly={readOnly}
              value={fxRate.rate}
              onUpdate={onUpdateFxRate}
              validationIssues={fxRateEntity.validationIssues ?? []}
            />
          </DialogField>
        </DialogRow>
      </ModalContent>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton type={TextButtonType.Primary} disabled={readOnly || hasValidationIssues} onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const FxRateDialog = connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(FxRateDialogComponent)
